import { toggleNavigation as toggleNavigationAction } from '../navigation/navigation.store';
import { ActionConfig } from './action';
import css from './titleBar.module.less';
import { titleBarStore } from './titleBar.store';
import { ActionLoadingIcon } from './useActionLoadingIndicator';
import { WipTextField } from '@/components/wipTextField';
import config from '@/config/config';
import { useStoreGuid } from '@/lib/hooks';
import storeService from '@/store/store.service';
import type { TitleBarProps } from '@/titleBar/titleBar';
import { t } from '@/translations/translator';
import CustomerMark from '@/util/CustomerMark';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faBars, faChevronLeft, faEllipsisV, faSearch, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Tooltip } from '@mui/material';
import { ClickOutside } from '@whiz-cart/ui-shared/clickOutside/clickOutside.component.tsx';
import useReturnLocation from '@whiz-cart/ui-shared/history/useReturnLocation';
import { overlayService } from '@whiz-cart/ui-shared/overlay/overlay.service';
import { NavLink } from '@whiz-cart/ui-shared/routing/navLink.component.tsx';
import { urlService } from '@whiz-cart/ui-shared/url/url.service';
import c from 'classnames';
import _ from 'lodash';
import React, { isValidElement, MouseEvent, useEffect, useState } from 'react';
export type { TitleBarProps } from '@/titleBar/titleBar';

export default function TitleBar() {
    const [menuOpen, setMenuOpen] = useState(false);

    const envColor = config.showEnvColor || undefined;

    const currentTitleBar = titleBarStore.useStore('current');
    const { back, autoBack = !!back, title, breakActionsEarly, collapse, guid, search, extraComponent, isCustomerMarked } = currentTitleBar;
    let { titleComponent } = currentTitleBar;
    const visibleActions = currentTitleBar.visibleActions ?? config.titleBarVisibleActions;

    const query = urlService.state.useStore((url) => {
        if (typeof search === 'object' && search?.value !== undefined) {
            return search.value ?? '';
        }
        return (url.hash.q ?? '') as string;
    });

    const [searchFocused, setSearchFocused] = useState(!!query);

    let actions = titleBarStore.useStore('actions');

    const { storeGuid } = useStoreGuid();
    const enableCustomerMark = storeService.getConfig(storeGuid).useCache()?.[0]?.enableCustomerMark;

    const returnLocation = useReturnLocation({
        exclude: typeof autoBack !== 'boolean' ? autoBack.exclude : undefined,
        onlyExplicit: !autoBack,
    });
    const backUrl = returnLocation ?? (typeof back === 'function' ? undefined : back);
    const backIsActive = !!backUrl || !!back;

    useEffect(() => {
        setMenuOpen(false);
        setSearchFocused(!!query);
        // oxlint-disable-next-line exhaustive-deps
    }, [guid]);

    function openMenu() {
        setMenuOpen(true);
    }

    function closeMenu() {
        if (menuOpen) setMenuOpen(false);
    }

    function filterActionsForTitlebar(actions: any[], titleBar: Partial<TitleBarProps>) {
        if (titleBar.onlyKeepActionsWithLinkId) {
            return actions.filter((a) => a?.titleBarLinkId === titleBar.onlyKeepActionsWithLinkId);
        } else {
            return actions;
        }
    }

    function handleBackClick(e: MouseEvent<HTMLAnchorElement>) {
        if (back instanceof Function) {
            back(e);
            e.preventDefault();
            return;
        }
    }

    function toggleNavigation(event: React.MouseEvent<SVGSVGElement>) {
        event.nativeEvent.stopImmediatePropagation();
        toggleNavigationAction();
    }

    function renderAction({ icon, label, action, actionProps, className, id, isLoading, disabled }: ActionConfig, inMenu?: boolean) {
        const isDisabled = !!disabled;

        const loadingIcon = <ActionLoadingIcon />;
        const regularIcon = isValidElement(icon) ? icon : icon ? <FontAwesomeIcon fixedWidth icon={icon as IconProp} /> : null;
        const common = {
            'data-testid': `titleBarElement-${id}`,
            className: c(css.action, { [css.disabled]: isDisabled }, className),
            children: (
                <>
                    {isLoading ? loadingIcon : regularIcon}
                    {inMenu && label}
                </>
            ),
            ...actionProps,
        };

        function handleNavLinkClick(event: React.MouseEvent<HTMLSpanElement>) {
            if (isDisabled) {
                event.preventDefault();

                if (typeof disabled !== 'boolean') {
                    overlayService.overlay({
                        message: <Box sx={{ pt: 4 }}>{disabled}</Box>,
                        canClose: true,
                        actions: [{ label: t('ok') }],
                        size: 's',
                    });
                }
                return;
            }
        }

        function handleClick(event: React.MouseEvent<HTMLDivElement>) {
            if (isDisabled) {
                if (typeof disabled !== 'boolean') {
                    overlayService.overlay({
                        message: <Box sx={{ pt: 4 }}>{disabled}</Box>,
                        canClose: true,
                        actions: [{ label: t('ok') }],
                        size: 's',
                    });
                }
                return;
            }

            if (action instanceof Function) {
                action(event);
            }
        }

        let innerElement;
        if (typeof action !== 'function') {
            innerElement = (
                <span onClick={handleNavLinkClick}>
                    <NavLink {...common} to={action} />
                </span>
            );
        } else {
            innerElement = <div {...common} onClick={handleClick} />;
        }

        return (
            <Tooltip
                key={id}
                title={isDisabled ? (typeof disabled === 'boolean' ? undefined : disabled) : inMenu ? '' : (label ?? '')}
                disableInteractive
            >
                {innerElement}
            </Tooltip>
        );
    }

    if (search) {
        const isOpen = query !== '' || searchFocused;

        if (isOpen) {
            titleComponent = (
                <WipTextField
                    className={css.search}
                    autoFocus
                    value={query}
                    onChange={(value) => {
                        urlService.pushUrl({}, { q: value === '' ? undefined : value });
                    }}
                    debounce={500}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    clearable={false}
                    {...(typeof search === 'object' ? search : undefined)}
                />
            );
        }

        const searchAction = (
            !isOpen
                ? {
                      id: '#search',
                      index: -1,
                      icon: faSearch,
                      label: t('search'),
                      action: () => setSearchFocused(true),
                  }
                : {
                      id: '#search',
                      index: -1,
                      icon: faTimes,
                      label: t('close'),
                      action: () => {
                          setSearchFocused(false);
                          if (typeof search === 'object' && search.onChange) {
                              search.onChange?.('');
                          } else {
                              urlService.pushUrl({}, { q: undefined });
                          }
                      },
                      actionProps: {
                          onPointerDown: (e) => e.preventDefault(),
                      },
                      className: c({ [css.searchIconClear]: actions.length > 0 }),
                  }
        ) satisfies ActionConfig;

        actions = [searchAction, ...actions];
    }

    actions = filterActionsForTitlebar(actions, currentTitleBar);
    const allActions = _.orderBy(actions, ['index', 'name', 'id']);
    const actionsTop =
        allActions.length <= (breakActionsEarly ? visibleActions - 1 : visibleActions)
            ? allActions
            : [...allActions.slice(0, Math.max(0, visibleActions - 1)), { id: '#overflow', icon: faEllipsisV, action: openMenu }];
    const actionsMenu =
        actions.length <= (breakActionsEarly ? visibleActions - 1 : visibleActions) ? undefined : allActions.slice(visibleActions - 1);

    return (
        <div data-testid="titleBarContainer" className={c(css.titleBar, { [css.collapsed]: collapse })}>
            {backIsActive && (
                <NavLink data-testid="navigateBack" to={backUrl} onClick={handleBackClick}>
                    <FontAwesomeIcon icon={faChevronLeft} className={css.leftButton} />
                </NavLink>
            )}

            {!backIsActive && <FontAwesomeIcon icon={faBars} className={css.leftButton} onClick={toggleNavigation} />}

            {envColor && <div className={css.envFlag} style={{ borderLeftColor: envColor }} />}

            {titleComponent ?? <div className={css.titleComponent}>{title}</div>}

            {enableCustomerMark && isCustomerMarked && (
                <div>
                    <CustomerMark size={1.5} />
                </div>
            )}

            {extraComponent}

            <ClickOutside data-testid="titleBarActions" className={css.actions} onClick={closeMenu} onClickOutside={closeMenu}>
                {actionsTop.map((action) => renderAction(action))}
                {menuOpen && actionsMenu && (
                    <div data-testid="titleBarMenu" className={css.menu}>
                        {actionsMenu.map((action) => (
                            <React.Fragment key={action?.id}>{renderAction(action, true)}</React.Fragment>
                        ))}
                    </div>
                )}
            </ClickOutside>
        </div>
    );
}
